import { ITime } from '../models/time';
import { StringHelper } from '../utils/string-helper';

/**
 * Helper functions for Date conversion and comparison
 *
 * @export
 * @class DateHelper
 */
export class DateHelper {

  /**
   * Returns true if the given dates are on the same day
   *
   * @static
   * @param {Date} a
   * @param {Date} b
   * @returns
   * @memberof DateHelper
   */
  public static AreSameDay(a: Date, b: Date) {
    return a.getDay() === b.getDay() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }

  /**
   * Returns true if the given dates are the same
   *
   * @static
   * @param {Date} a
   * @param {Date} b
   * @returns
   * @memberof DateHelper
   */
  public static AreSameDate(a: Date, b: Date) {
    return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }

  /**
   * Returns the given date with time set to 00:00:00.000
   *
   * @static
   * @param {Date} datetime
   * @returns {Date}
   * @memberof DateHelper
   */
  public static StartOfDay(datetime: Date): Date {
    const date = new Date(datetime);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Returns the given date with time set to 23:59:59.999
   *
   * @static
   * @param {Date} datetime
   * @returns {Date}
   * @memberof DateHelper
   */
  public static EndOfDay(datetime: Date): Date {
    const date = new Date(datetime);
    date.setHours(23, 59, 59, 999);
    return date;
  }

  /**
   * returns seconds since 1970-01-01 UTC
   *
   * @static
   * @param {Date} datetime
   * @returns {number}
   * @memberof DateHelper
   */
  public static getUnixTime(datetime: Date): number {
    return Math.floor(datetime.getTime() / 1000);
  }

  /**
   * returns number of minutes of given date since 0:00
   *
   * @static
   * @param {Date} value
   * @returns
   * @memberof DateHelper
   */
  public static getMinutesOfDay(value: Date | ITime): number {
    if (value instanceof Date) {
      return value.getHours() * 60 + value.getMinutes();
    }
    return value.hour * 60 + value.minute;
  }

  /**
   * set the time of given date-instance to given minutes of day
   *
   * @static
   * @param {number} minutes
   * @param {Date} datetime
   * @memberof DateHelper
   */
  public static setTimeFromMinutesOfDay(minutes: number, datetime: Date) {
    datetime.setHours(Math.floor(minutes / 60));
    datetime.setMinutes(Math.floor(minutes % 60));
    datetime.setSeconds(0);
    datetime.setMilliseconds(0);

    return datetime;
  }

  /**
   * Calculates the difference of weekdays of the two dates (eg. Mon and Wed = 2)
   *
   * @static
   * @param {Date} date1 - Minuend
   * @param {Date} date2 - Subtrahend
   * @returns {number}
   * @memberof DateHelper
   */
  public static getWeekdayDifference(date1: Date, date2: Date): number {
    let index1 = date1.getDay() - 1; // mo = 0, sa = 5
    if (index1 === -1) {
      index1 = 6; // so = 6;
    }

    let index2 = date2.getDay() - 1; // mo = 0, sa = 5
    if (index2 === -1) {
      index2 = 6; // so = 6;
    }

    return index1 - index2;
  }

  /**
   * Parse any datetime value to a local Date object
   * This should prevent any localtime/UTC differences in various browsers
   *
   * @static
   * @param {*} object
   * @returns {Date}
   * @memberof DateHelper
   */
  public static parseDateString(object: any): Date {
    if (object === null) {
      return null;
    }

    if (object instanceof Date) {
      return new Date(object);
    }

    const parts = object.split(/[\-T:.Z]/).filter((x: string) => x !== '').map(Number);
    if (parts.length === 6) {
      parts[6] = 0;
    }

    if (parts.length !== 7) {
      console.warn('No valid date: ' + String(object));
      return null;
    }

    return new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5], parts[6]);
  }

  /**
   * Converts ISO 8601 to {Date}
   *
   * @static
   * @param {string} object
   * @returns {Date}
   * @memberof DateHelper
   */
  public static parseIsoDateString(object: string): Date {
    return new Date(Date.parse(object));
  }

  /**
   * Returns the smaller of two <see cref="DateTime"/> structs.
   *
   * @static
   * @param {Date} date1
   * @param {Date} date2
   * @returns {Date}
   * @memberof DateHelper
   */
  public static Min(date1: Date, date2: Date): Date {
    return date1 <= date2 ? date1 : date2;
  }

  /**
   * Returns the larger of two <see cref="DateTime"/> structs.
   *
   * @static
   * @param {Date} date1
   * @param {Date} date2
   * @returns {Date}
   * @memberof DateHelper
   */
  public static Max(date1: Date, date2: Date): Date {
    return date1 >= date2 ? date1 : date2;
  }

  /**
   * Get current date or specific if in debug-mode
   *
   * @private
   * @returns {Date}
   * @memberof RoomReservationComponent
   */
  public static getCurrentDate(): Date {
    const now = new Date();

    return now;
  }

  /**
   * Returns true if the given date-ranges intersect each other
   *
   * @static
   * @param {Date} start1
   * @param {Date} end1
   * @param {Date} start2
   * @param {Date} end2
   * @returns {boolean}
   * @memberof DateHelper
   */
  public static Intersects(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
    return start1 < end2 && end1 > start2;
  }

  /**
   * creates hours and minutes stamp from elapsed minutes since midnight (00:00)
   * @param {number} minPastMidnight number of minutes elapsed since midnight today
   * @returns {string}
   */
  public static createDisplayTime(minPastMidnight: number) {
    const hrs = Math.floor(minPastMidnight / 60) % 24;
    const min = minPastMidnight % 60;
    return StringHelper.formatTime(hrs, min);
  }

  /**
   * converts a 24H display time format to minutes elapsed since midnight today
   * @param {string} displayTime a valid 24H time format
   */
  public static convertDisplayTimeToMinsPastMidnight(displayTime: string) {
    const [hours, minutes] = displayTime.split(':', 2).map(p => Number(p)).filter(n => Number.isFinite(n));

    if (hours < 0 || hours > 23) { throw new Error('Invalid hour section in `displayTime` format argument'); }
    if (minutes < 0 || minutes > 60) { throw new Error('Invalid minutes section in `displayTime` format argument'); }

    return (60 * hours) + minutes;
  }

  /**
   * returns the number of minutes passed since midnight today
   * @returns {number} the minute of day
   */
  public static getCurrentMinuteOfDay() {
    const todaysDate = new Date();

    const hours = todaysDate.getHours();
    const minutes = todaysDate.getMinutes();
    const minuteOfDay = (hours * 60) + minutes + Math.round(todaysDate.getSeconds() / 60);

    return minuteOfDay;
  }

  public static getWeekDateRangeByDate(date: Date | null, preferredWeekStart: number = 0): [Date, Date] {
    const originDate = date !== null ? new Date(date) : new Date();

    if (isNaN(originDate.valueOf())) { return null; }

    // First day is the day of the month - the day of the week
    const firstDay = originDate.getDate() - originDate.getDay() + preferredWeekStart;
    const startDate = new Date(originDate);
    startDate.setDate(firstDay);

    // should be 6, but 7 -1 ms will push time of date to 23:59:59.999
    const lastDay = firstDay + 7;
    const endDate = new Date(startDate);

    endDate.setDate(lastDay);

    return [
      new Date(startDate),
      new Date(endDate.getTime() - 1)
    ];
  }

  public static getWeekNumberByDate(date: Date | null): number {

    const dateClone = date !== null
      ? new Date(
        Date.UTC(date.getFullYear(),
          date.getMonth(),
          date.getDate())
      )

      : new Date();

    dateClone.setUTCDate(dateClone.getUTCDate() + 4 - (dateClone.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(dateClone.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((dateClone.getTime() - yearStart.getDate()) / 86400000) + 1) / 7);

    return weekNo;
  }

  public static getCHTimeString(date: Date) {
    return date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  }
}
